import { useCallback } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';

import { SideNavigationMenuGroup } from '@project44/manifest-ds';
import { useTracking } from '@project44/ui-utils';

import isRouteActive from './isRouteActive';

type WrappedSideNavigationMenuGroupProps = Omit<
  React.ComponentProps<typeof SideNavigationMenuGroup>,
  'onClick'
> & {
  menuId: string;
  url: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>, menuId: string) => void;
};

export default function SideBarMenuGroup({
  menuId,
  url,
  isExpanded,
  onClick,
  ...otherProps
}: WrappedSideNavigationMenuGroupProps) {
  const location = useLocation();
  const tracker = useTracking();

  const isSelected = isRouteActive(location.pathname, url);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      tracker.track({
        eventName: `side-nav.menu-group.${
          isSelected ? 'contracted' : 'expanded'
        }`,
        properties: { menuId },
      });
      onClick?.(event, menuId);
    },
    [isSelected, menuId, onClick, tracker],
  );

  return (
    <SideNavigationMenuGroup
      isExpanded={isExpanded && isSelected}
      data-testid={`side-nav-${menuId}${isSelected ? '-active' : ''}`}
      onClick={handleClick}
      {...otherProps}
    />
  );
}
